import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import {
	AddIcon,
	Button,
	Callout,
	Card,
	ChevronDownIcon,
	ChevronRightIcon,
	DeleteIcon,
	Empty,
	FlexGap,
	Heading3,
	IconButton,
	Input,
	Label,
	ListItem,
	Paragraph,
	Pill,
	Select,
	Separator,
	tokens,
} from '@treams/treams-ui';

import {
	type PlacementHelpAxis,
	type TemplatePlacementHelpQuestion,
	asPlacementHelpAxis,
	placementHelpQuestionsAreReady,
	questionsOnTemplate,
} from '../talentReviewPlacementHelp';

const SKIP_TICK = 'skip';

export type PlacementHelpQuestionForm = {
	id: string;
	axis: PlacementHelpAxis;
	question: graphql.LocalizedString;
	answers: {
		id: string;
		label: graphql.LocalizedString;
		tickIndex?: number;
	}[];
};

type FormValues = {
	languageID: string;
	xAxis: { label: graphql.LocalizedString; ticks: graphql.LocalizedString[] };
	yAxis: { label: graphql.LocalizedString; ticks: graphql.LocalizedString[] };
	placementHelpQuestions: PlacementHelpQuestionForm[];
};

function localizedText(value: graphql.LocalizedString | undefined, languageID: string) {
	return value?.find((item) => item.languageID === languageID)?.text ?? '';
}

function setLocalizedText(
	value: graphql.LocalizedString | undefined,
	languageID: string,
	text: string,
): graphql.LocalizedString {
	return [...(value?.filter((item) => item.languageID !== languageID) ?? []), { languageID, text }];
}

function emptyAnswer(tickIndex?: number): PlacementHelpQuestionForm['answers'][number] {
	return {
		id: crypto.randomUUID(),
		label: [],
		...(typeof tickIndex === 'number' ? { tickIndex } : {}),
	};
}

export function emptyPlacementHelpQuestion(
	axis: PlacementHelpAxis = 'X',
): PlacementHelpQuestionForm {
	return {
		id: crypto.randomUUID(),
		axis,
		question: [],
		answers: [emptyAnswer(0), emptyAnswer()],
	};
}

export function placementHelpQuestionsFromTemplate(
	template?: unknown,
): PlacementHelpQuestionForm[] {
	return questionsOnTemplate<TemplatePlacementHelpQuestion>(template).map((question) => ({
		id: question.id,
		axis: asPlacementHelpAxis(question.axis),
		question: question.question ?? [],
		answers: question.answers.map((answer) => ({
			id: answer.id,
			label: answer.label ?? [],
			...(typeof answer.tickIndex === 'number' ? { tickIndex: answer.tickIndex } : {}),
		})),
	}));
}

export function placementHelpQuestionsInput(
	questions: PlacementHelpQuestionForm[],
): NonNullable<graphql.CreateTalentReviewTemplateInput['placementHelpQuestions']> {
	return questions.map((question) => ({
		id: question.id,
		axis: graphql.TalentReviewPlacementHelpAxis[question.axis],
		question: question.question,
		answers: question.answers.map((answer) => ({
			id: answer.id,
			label: answer.label,
			tickIndex: typeof answer.tickIndex === 'number' ? answer.tickIndex : null,
		})),
	}));
}

export default function TalentReviewPlacementHelpEditor() {
	const { t } = useTranslation();
	const formik = useFormikContext<FormValues>();
	const languageID = formik.values.languageID;
	const questions = formik.values.placementHelpQuestions;
	const xTicks = formik.values.xAxis.ticks;
	const yTicks = formik.values.yAxis.ticks;
	const ready = placementHelpQuestionsAreReady(questions, languageID);
	const [expandedIDs, setExpandedIDs] = useState<ReadonlySet<string>>(() => new Set());

	const setQuestions = (next: PlacementHelpQuestionForm[]) => {
		void formik.setFieldValue('placementHelpQuestions', next);
	};

	const toggleQuestion = (id: string) => {
		setExpandedIDs((current) => {
			const next = new Set(current);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const axisLabel = (axis: PlacementHelpAxis) => {
		const label = localizedText(
			axis === 'X' ? formik.values.xAxis.label : formik.values.yAxis.label,
			languageID,
		);
		return (
			label ||
			t(
				axis === 'X'
					? 'talentReviews.v2.template.edit.placementHelp.axisX'
					: 'talentReviews.v2.template.edit.placementHelp.axisY',
			)
		);
	};

	const tickOptions = (axis: PlacementHelpAxis) => {
		const ticks = axis === 'X' ? xTicks : yTicks;
		return [
			{
				value: SKIP_TICK,
				title: t('talentReviews.v2.template.edit.placementHelp.skipTick'),
			},
			...ticks.map((tick, index) => ({
				value: String(index),
				title:
					localizedText(tick, languageID) ||
					t('talentReviews.v2.template.edit.placementHelp.tickFallback', { index: index + 1 }),
			})),
		];
	};

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.group} mt={4} mb={2} maxWidth={720}>
			<Separator />
			<Box>
				<Heading3>{t('talentReviews.v2.template.edit.placementHelp.title')}</Heading3>
				<Paragraph small>{t('talentReviews.v2.template.edit.placementHelp.description')}</Paragraph>
			</Box>

			{questions.length > 0 && !ready && (
				<Callout color="warning">
					{t('talentReviews.v2.template.edit.placementHelp.incomplete')}
				</Callout>
			)}

			{questions.length === 0 ? (
				<Empty.Card
					title={t('talentReviews.v2.template.edit.placementHelp.emptyTitle')}
					description={t('talentReviews.v2.template.edit.placementHelp.emptyDescription')}
				/>
			) : (
				<Card p={0}>
					{questions.map((question, questionIndex) => {
						const open = expandedIDs.has(question.id);
						const questionTitle = localizedText(question.question, languageID);

						return (
							<ListItem
								key={question.id}
								aria-expanded={open}
								onClick={() => toggleQuestion(question.id)}
								icon={open ? <ChevronDownIcon /> : <ChevronRightIcon />}
								title={
									questionTitle ||
									t('talentReviews.v2.template.edit.placementHelp.questionItem', {
										index: questionIndex + 1,
									})
								}
								trailing={
									<>
										{!open && <Pill small>{axisLabel(question.axis)}</Pill>}
										<IconButton
											type="destructive"
											icon={<DeleteIcon />}
											aria-label={t('talentReviews.v2.template.edit.placementHelp.removeQuestion')}
											onClick={(event) => {
												event.stopPropagation();
												setQuestions(questions.filter((_, index) => index !== questionIndex));
												setExpandedIDs((current) => {
													const next = new Set(current);
													next.delete(question.id);
													return next;
												});
											}}
										/>
									</>
								}
							>
								{open && (
									<Box
										px={`${tokens.padding.container}px`}
										pb={`${tokens.gap.group}px`}
										onClick={(event) => event.stopPropagation()}
									>
										<FlexGap flexDirection="column" gap={tokens.gap.list}>
											<FlexGap flexDirection="column" gap={tokens.gap.element} width={240}>
												<Label>{t('talentReviews.v2.template.edit.placementHelp.axisLabel')}</Label>
												<Select
													small
													aria-label={t('talentReviews.v2.template.edit.placementHelp.axis')}
													options={[
														{ value: 'X', title: axisLabel('X') },
														{ value: 'Y', title: axisLabel('Y') },
													]}
													selectedValue={question.axis}
													onChange={(value) => {
														const axis = asPlacementHelpAxis(String(value ?? 'X'));
														const tickCount = (axis === 'X' ? xTicks : yTicks).length;
														setQuestions(
															questions.map((item, index) =>
																index === questionIndex
																	? {
																			...item,
																			axis,
																			answers: item.answers.map((answer) => ({
																				...answer,
																				tickIndex:
																					typeof answer.tickIndex === 'number' &&
																					answer.tickIndex < tickCount
																						? answer.tickIndex
																						: undefined,
																			})),
																		}
																	: item,
															),
														);
													}}
												/>
											</FlexGap>

											<Input
												label={t('talentReviews.v2.template.edit.placementHelp.question')}
												value={localizedText(question.question, languageID)}
												onChange={(value) =>
													setQuestions(
														questions.map((item, index) =>
															index === questionIndex
																? {
																		...item,
																		question: setLocalizedText(item.question, languageID, value),
																	}
																: item,
														),
													)
												}
											/>
											<Paragraph small>
												{t('talentReviews.v2.template.edit.placementHelp.questionHint')}
											</Paragraph>

											<Heading3 mb={0}>
												{t('talentReviews.v2.template.edit.placementHelp.answers')}
											</Heading3>
											{question.answers.map((answer, answerIndex) => (
												<FlexGap key={answer.id} alignItems="flex-end" gap={tokens.gap.list}>
													<Box flex={1} minWidth={0}>
														<Input
															label={t('talentReviews.v2.template.edit.placementHelp.answer')}
															value={localizedText(answer.label, languageID)}
															onChange={(value) =>
																setQuestions(
																	questions.map((item, index) =>
																		index === questionIndex
																			? {
																					...item,
																					answers: item.answers.map((option, optionIndex) =>
																						optionIndex === answerIndex
																							? {
																									...option,
																									label: setLocalizedText(
																										option.label,
																										languageID,
																										value,
																									),
																								}
																							: option,
																					),
																				}
																			: item,
																	),
																)
															}
														/>
													</Box>
													<Box width={200} pb="1px">
														<Select
															small
															aria-label={t('talentReviews.v2.template.edit.placementHelp.tick')}
															options={tickOptions(question.axis)}
															selectedValue={
																typeof answer.tickIndex === 'number'
																	? String(answer.tickIndex)
																	: SKIP_TICK
															}
															onChange={(value) => {
																const tickIndex =
																	value === SKIP_TICK || value === undefined
																		? undefined
																		: Number(value);
																setQuestions(
																	questions.map((item, index) =>
																		index === questionIndex
																			? {
																					...item,
																					answers: item.answers.map((option, optionIndex) =>
																						optionIndex === answerIndex
																							? {
																									...option,
																									tickIndex:
																										typeof tickIndex === 'number' &&
																										!Number.isNaN(tickIndex)
																											? tickIndex
																											: undefined,
																								}
																							: option,
																					),
																				}
																			: item,
																	),
																);
															}}
														/>
													</Box>
													<IconButton
														type="destructive"
														icon={<DeleteIcon />}
														disabled={question.answers.length < 2}
														aria-label={t(
															'talentReviews.v2.template.edit.placementHelp.removeAnswer',
														)}
														onClick={() =>
															setQuestions(
																questions.map((item, index) =>
																	index === questionIndex
																		? {
																				...item,
																				answers: item.answers.filter(
																					(_, optionIndex) => optionIndex !== answerIndex,
																				),
																			}
																		: item,
																),
															)
														}
													/>
												</FlexGap>
											))}
											<Box>
												<Button
													type="secondary"
													small
													icon={<AddIcon />}
													title={t('talentReviews.v2.template.edit.placementHelp.addAnswer')}
													onClick={() =>
														setQuestions(
															questions.map((item, index) =>
																index === questionIndex
																	? { ...item, answers: item.answers.concat(emptyAnswer()) }
																	: item,
															),
														)
													}
												/>
											</Box>
										</FlexGap>
									</Box>
								)}
							</ListItem>
						);
					})}
				</Card>
			)}

			<Box>
				<Button
					type="secondary"
					icon={<AddIcon />}
					title={t('talentReviews.v2.template.edit.placementHelp.addQuestion')}
					onClick={() => {
						const question = emptyPlacementHelpQuestion();
						setQuestions(questions.concat(question));
						setExpandedIDs((current) => new Set(current).add(question.id));
					}}
				/>
			</Box>
		</FlexGap>
	);
}
